'use client';

import { useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import styles from './InviteGallery.module.css';

// Prototype gallery (ultrahuman-invite-page/assets/carousel), uploaded as-is to
// web_v2/affiliate/carousel/. PRO and AIR shots alternate; crops match the prototype.
const BASE = 'web_v2/affiliate/carousel';
const SLIDES = [
  {
    file: '1.jpg',
    altKey: 'proRope',
    width: 1600,
    height: 2240,
    position: '50% 50%',
  },
  {
    file: 'air-1.jpg',
    altKey: 'airCup',
    width: 1600,
    height: 1600,
    position: '58% 50%',
  },
  {
    file: '2.jpg',
    altKey: 'proTrail',
    width: 1600,
    height: 2240,
    position: '50% 50%',
  },
  {
    file: 'air-2.jpg',
    altKey: 'airCable',
    width: 1500,
    height: 2249,
    position: '30% 35%',
  },
  {
    file: '3.jpg',
    altKey: 'proRain',
    width: 1600,
    height: 2240,
    position: '50% 50%',
  },
  {
    file: 'air-3.jpg',
    altKey: 'airTennis',
    width: 832,
    height: 1029,
    position: '50% 55%',
  },
  {
    file: '4.jpg',
    altKey: 'proClimb',
    width: 1600,
    height: 2240,
    position: '50% 50%',
  },
  {
    file: 'air-4.jpg',
    altKey: 'airGym',
    width: 1536,
    height: 2752,
    position: '58% 68%',
  },
] as const;

const LOOP_MS = 70000;

// Eases the loop to a stop on hover and back up to speed on leave, instead of
// freezing and jumping (prototype behaviour, Web Animations API).
const useEasedMarquee = (ref: React.RefObject<HTMLDivElement | null>) => {
  useEffect(() => {
    const el = ref.current;
    if (!el || !el.animate) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const anim = el.animate(
      [{ transform: 'translateX(0)' }, { transform: 'translateX(-50%)' }],
      { duration: LOOP_MS, iterations: Infinity, easing: 'linear' },
    );
    let raf = 0;
    const ease = (t: number) =>
      t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    const rampTo = (target: number, ms: number) => {
      cancelAnimationFrame(raf);
      const from = anim.playbackRate;
      const t0 = performance.now();
      const step = (now: number) => {
        const p = Math.min(1, (now - t0) / ms);
        anim.playbackRate = from + (target - from) * ease(p);
        if (p < 1) raf = requestAnimationFrame(step);
      };
      step(t0);
    };
    const stop = () => rampTo(0, 900);
    const resume = () => rampTo(1, 1100);
    el.addEventListener('mouseenter', stop);
    el.addEventListener('mouseleave', resume);
    // No work while the gallery is off screen.
    const visibility = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) anim.play();
      else anim.pause();
    });
    visibility.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      visibility.disconnect();
      el.removeEventListener('mouseenter', stop);
      el.removeEventListener('mouseleave', resume);
      anim.cancel();
    };
  }, [ref]);
};

export const InviteGallery = () => {
  const t = useTranslations('invite');
  const marqueeRef = useRef<HTMLDivElement>(null);
  useEasedMarquee(marqueeRef);

  const track = (hidden: boolean) => (
    <ul className={styles.track} aria-hidden={hidden || undefined}>
      {SLIDES.map((slide) => (
        <li key={slide.file} className={styles.slide}>
          <CustomImage
            src={`${BASE}/${slide.file}?v=1`}
            alt={hidden ? '' : t(`gallery.${slide.altKey}`)}
            width={slide.width}
            height={slide.height}
            sizes="(max-width: 760px) 220px, 27vw"
            loading="lazy"
            style={{ objectPosition: slide.position }}
            draggable={false}
          />
        </li>
      ))}
    </ul>
  );

  return (
    <section className={styles.gallery} aria-label={t('gallery.label')}>
      <div ref={marqueeRef} className={styles.marquee}>
        {track(false)}
        {track(true)}
      </div>
    </section>
  );
};
